export type Point2 = readonly [number, number];
export interface XZ { x: number; z: number }
export interface LinePoint { s: number; d: number; index: number }

function catmullRomClosed(p: readonly Point2[], perSpan: number): Point2[] {
  const n = p.length;
  const out: Point2[] = [];
  for (let k = 0; k < n; k++) {
    const p0 = p[(k - 1 + n) % n]!, p1 = p[k]!, p2 = p[(k + 1) % n]!, p3 = p[(k + 2) % n]!;
    for (let q = 0; q < perSpan; q++) {
      const t = q / perSpan, t2 = t * t, t3 = t2 * t;
      const f = (a: number, b: number, c: number, e: number) =>
        0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - e) * t2 + (-a + 3 * b - 3 * c + e) * t3);
      out.push([f(p0[0], p1[0], p2[0], p3[0]), f(p0[1], p1[1], p2[1], p3[1])]);
    }
  }
  return out;
}

/** Closed Catmull-Rom centreline, resampled to `count` equal arc-length steps. */
/** Scratch of the allocation-free queries below. */
const P2 = new Float64Array(4), TAN = new Float64Array(4);

export class CenterLine {
  readonly xs: Float64Array;
  readonly zs: Float64Array;
  readonly length: number;
  readonly count: number;
  readonly step: number;

  constructor(points: readonly Point2[], step = 1) {
    if (points.length < 3) throw new Error('centreline needs at least 3 points');
    const dense = catmullRomClosed(points, 32);
    const cum = new Float64Array(dense.length + 1);
    for (let i = 1; i <= dense.length; i++) {
      const a = dense[i - 1]!, b = dense[i % dense.length]!;
      cum[i] = cum[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1]);
    }
    this.length = cum[dense.length]!;
    this.count = Math.max(3, Math.round(this.length / step));
    this.step = this.length / this.count;
    this.xs = new Float64Array(this.count);
    this.zs = new Float64Array(this.count);
    let j = 0;
    for (let i = 0; i < this.count; i++) {
      const s = i * this.step;
      while (cum[j + 1]! < s) j++;
      const u = (s - cum[j]!) / (cum[j + 1]! - cum[j]!);
      const a = dense[j]!, b = dense[(j + 1) % dense.length]!;
      this.xs[i] = a[0] + (b[0] - a[0]) * u;
      this.zs[i] = a[1] + (b[1] - a[1]) * u;
    }
  }

  wrap(s: number): number {
    const L = this.length;
    return ((s % L) + L) % L;
  }

  pointAt(s: number): XZ {
    const f = this.wrap(s) / this.step;
    const i = Math.floor(f) % this.count;
    const j = (i + 1) % this.count;
    const t = f - Math.floor(f);
    return { x: this.xs[i]! + (this.xs[j]! - this.xs[i]!) * t, z: this.zs[i]! + (this.zs[j]! - this.zs[i]!) * t };
  }

  tangentAt(s: number): XZ {
    const a = this.pointAt(s - this.step), b = this.pointAt(s + this.step);
    const l = Math.hypot(b.x - a.x, b.z - a.z);
    return { x: (b.x - a.x) / l, z: (b.z - a.z) / l };
  }

  /** Signed curvature, > 0 turning left. */
  curvatureAt(s: number, h = 2): number {
    const t0 = this.tangentAt(s - h), t1 = this.tangentAt(s + h);
    return (t1.x * t0.z - t1.z * t0.x) / (2 * h);
  }

  /** Nearest point on the polyline. With a hint, only segments within `window` metres of it are searched. */
  closest(x: number, z: number, hint?: number, window = 40): LinePoint {
    const n = this.count;
    let first = 0, span = n;
    if (hint !== undefined) {
      const w = Math.ceil(window / this.step);
      first = Math.floor(this.wrap(hint) / this.step) - w;
      span = Math.min(n, 2 * w + 1);
    }
    let best = Infinity, bs = 0, bd = 0, bi = 0;
    for (let q = 0; q < span; q++) {
      const k = (((first + q) % n) + n) % n;
      const k1 = (k + 1) % n;
      const ax = this.xs[k]!, az = this.zs[k]!;
      const ex = this.xs[k1]! - ax, ez = this.zs[k1]! - az;
      const px = x - ax, pz = z - az;
      const len2 = ex * ex + ez * ez;
      const t = Math.min(1, Math.max(0, (px * ex + pz * ez) / len2));
      const qx = px - t * ex, qz = pz - t * ez;
      const dist = Math.hypot(qx, qz);
      if (dist < best) {
        best = dist;
        bi = k;
        bs = (k + t) * this.step;
        bd = (px * ez - pz * ex) >= 0 ? dist : -dist;
      }
    }
    return { s: this.wrap(bs), d: bd, index: bi };
  }

  /**
   * `closest` written into `out`, allocation-free (Math.sqrt for Math.hypot, so the last bit may differ from it): for
   * callers inside the 240 Hz step (simplified mode's speed planning). Needs a hint.
   */
  closestInto(x: number, z: number, hint: number, window: number, out: LinePoint): void {
    const n = this.count;
    const w = Math.ceil(window / this.step);
    const first = Math.floor(this.wrap(hint) / this.step) - w;
    const span = Math.min(n, 2 * w + 1);
    let best = Infinity, bs = 0, bd = 0, bi = 0;
    for (let q = 0; q < span; q++) {
      const k = (((first + q) % n) + n) % n;
      const k1 = (k + 1) % n;
      const ax = this.xs[k]!, az = this.zs[k]!;
      const ex = this.xs[k1]! - ax, ez = this.zs[k1]! - az;
      const px = x - ax, pz = z - az;
      const len2 = ex * ex + ez * ez;
      const t = Math.min(1, Math.max(0, (px * ex + pz * ez) / len2));
      const qx = px - t * ex, qz = pz - t * ez;
      const dist = Math.sqrt(qx * qx + qz * qz);
      if (dist < best) { best = dist; bi = k; bs = (k + t) * this.step; bd = (px * ez - pz * ex) >= 0 ? dist : -dist; }
    }
    out.s = this.wrap(bs); out.d = bd; out.index = bi;
  }

  /** `curvatureAt(s, 2)` without allocating (Math.sqrt for Math.hypot): for callers inside the 240 Hz step. */
  curvatureAtInto(s: number): number {
    this.tangentTo(s - 2, 0);
    this.tangentTo(s + 2, 2);
    return (TAN[2]! * TAN[1]! - TAN[3]! * TAN[0]!) / 4;
  }

  /** Heading (atan2(x, z), the Placement convention) of the line's direction at s, without allocating. */
  tangentHeading(s: number): number {
    this.tangentTo(s, 0);
    return Math.atan2(TAN[0]!, TAN[1]!);
  }

  /** Unit tangent at s into TAN[o], TAN[o + 1] (as tangentAt, from the points a step either side). */
  private tangentTo(s: number, o: number): void {
    this.pointTo(s - this.step, 0);
    this.pointTo(s + this.step, 2);
    const dx = P2[2]! - P2[0]!, dz = P2[3]! - P2[1]!;
    const l = Math.sqrt(dx * dx + dz * dz);
    TAN[o] = dx / l; TAN[o + 1] = dz / l;
  }

  /** pointAt(s) into P2[o], P2[o + 1]. */
  private pointTo(s: number, o: number): void {
    const f = this.wrap(s) / this.step;
    const i = Math.floor(f) % this.count;
    const j = (i + 1) % this.count;
    const t = f - Math.floor(f);
    P2[o] = this.xs[i]! + (this.xs[j]! - this.xs[i]!) * t; P2[o + 1] = this.zs[i]! + (this.zs[j]! - this.zs[i]!) * t;
  }
}
